/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import type { PointTooltip } from '@sportninja/common/features/profile/stats/utils/pointTooltip'

import Text from 'src/components/ts-components/Text'

interface ChartPointTooltipProps {
  tooltip: PointTooltip
  /** The hovered line's colour, shown as a dot beside the value. */
  color: string
  /** Point position in px from the chart's top-left corner. */
  left: number
  top: number
}

/** Value bubble floating just above a hovered chart point. */
const ChartPointTooltip = ({
  tooltip,
  color,
  left,
  top,
}: ChartPointTooltipProps) => {
  'use memo'

  return (
    <div role='tooltip' css={[bubbleStyles, positionStyles(left, top)]}>
      <Text variant='paragraphXSmall' weight='medium' color={colors.NEUTRAL_50}>
        {tooltip.title}
      </Text>
      <div css={valueRowStyles}>
        <span aria-hidden='true' css={dotStyles(color)} />
        <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
          {tooltip.value}
        </Text>
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/** Gap between the bubble and the point it describes. */
const POINT_GAP = 12
const DOT_SIZE = 8

const bubbleStyles = css`
  position: absolute;
  transform: translate(-50%, calc(-100% - ${POINT_GAP}px));
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_500};
  white-space: nowrap;
  pointer-events: none;
  z-index: 1;
`

const positionStyles = (left: number, top: number) => css`
  left: ${left}px;
  top: ${top}px;
`

const valueRowStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const dotStyles = (color: string) => css`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${color};
`

export default ChartPointTooltip
